import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import Draggable, { DraggableData } from 'react-draggable'
import { makeStyles } from '@mui/styles'

import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import getRelativePosition from '@/modules/signature/_helpers/getRelativePosition'
import getAbsolutePosition from '@/modules/signature/_helpers/getAbsolutePosition'
import ThemeInterface from '@/theme-interface'
import { SIGNER_COLOR_CODES } from '@/redux/ducks/signature/edit'
import { getContrastText } from '@/modules/base/helpers/getContrastText'
import { alpha } from '@mui/material'
import clsx from 'clsx'

const defaultMarkerHeight = 30

type StyleConfig = {
  scaled?: boolean
  heightPx?: number
  aspectRatio?: number | string
}

type BasePositionMarkerStyleProps = {
  active: boolean,
  isDragging: boolean,
  signatureColor: string,
  theme?: ThemeInterface,
}

const itemStyle = ({
  active,
  isDragging,
  theme,
  signatureColor = SIGNER_COLOR_CODES[0],
}: BasePositionMarkerStyleProps) => {
  const contrastColor = getContrastText(signatureColor)
  const signatureColorAlpha = alpha(signatureColor, 0.8)

  const defaultStyle = {
    border: `1px solid ${signatureColorAlpha}`,
    background: signatureColorAlpha,
    opacity: 1,
    color: contrastColor,
    cursor: 'pointer',
  }

  const activeStyle = {
    opacity: 1,
    background: signatureColorAlpha,
    color: contrastColor,
    zIndex: 10000,
    cursor: 'pointer',
    border: `1px solid ${theme.black}`,
  }

  const draggingStyle = {
    opacity: 0.5,
    background: signatureColor,
    border: '1px dashed #fff',
    color: contrastColor,
    cursor: 'grabbing',
  }

  if (active && isDragging) return draggingStyle
  if (active && !isDragging) return activeStyle

  return defaultStyle
}

export type MarkerBaseProps = {
  i: number,
  active: boolean,
  position: { x: number, y: number, page: number },
  signed?: boolean,
  signatureColor: string,
  containerClassName?: string,
  innerClassName?: string,
  disabled?: boolean,
  styleConfig?: StyleConfig,
  children: React.ReactNode | ((args: { markerChildRef: React.RefObject<HTMLDivElement> }) => React.ReactNode),
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void,
  onDragStart?: () => void,
  onDragStop?: (args: { xPercent: number, yPercent: number }) => void,
}

type StylesProps = BasePositionMarkerStyleProps & {
  pageScaleFactor: number,
  styleConfig: StyleConfig,
}

const useStyles = makeStyles<ThemeInterface, StylesProps>((theme) => ({
  outer: (props) => ({
    position: 'absolute',
    zIndex: 9999,
    pointerEvents: 'all',
    ...(props?.styleConfig?.scaled
      ? {
        height: `calc(${props.styleConfig.heightPx || defaultMarkerHeight}px * ${props.pageScaleFactor || 1})`,
        aspectRatio: props.styleConfig.aspectRatio ?? 'unset',
      }
      : {
        height: props?.styleConfig?.heightPx ?? 'auto',
      }),
  }),
  markerBorder: (props) => {
    return itemStyle({ ...props, theme })
  },
  inner: (props) => ({
    ...(props?.styleConfig?.scaled
      ? {
        transform: `scale(${props.pageScaleFactor || 1})`,
        transformOrigin: 'top left',
        aspectRatio: props.styleConfig.aspectRatio ?? 'unset',
      }
      : {}),
    height: props.styleConfig.heightPx ?? defaultMarkerHeight,
    padding: '0px 7px',
    letterSpacing: '0.36px',
    lineHeight: 1,
    fontSize: 9,
    cursor: 'pointer',
    textAlign: 'left',
    color: '#313131',
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    gap: 7,
    borderRadius: 2,
    opacity: 1,
  }),
}), { name: 'MarkerBase' })

const MarkerBase = React.forwardRef((props: MarkerBaseProps, forwardRef) => {
  const { i, active, position, signed, signatureColor, containerClassName, innerClassName, disabled, styleConfig = { scaled: true, heightPx: defaultMarkerHeight },
    children, onClick, onDragStart, onDragStop } = props

  const markerChildRef = useRef<HTMLDivElement>(null)
  const draggedRef = useRef<boolean>(false)
  const [isDragging, setIsDragging] = useState<boolean>(false)

  const {
    setCurrentItemIndex,
    setScrollIntoView,
    currentItemIndex,
    updateItem,
    item: activeItem,
    placingMode,
    pager,
    pageSize,
    pageScaleFactor,
    scrollIntoView,
  } = useSignatureContext()

  const classes = useStyles({ active, isDragging, signatureColor, pageScaleFactor, styleConfig })

  const containerWidth = pageSize.width
  const containerHeight = pageSize.height

  const getPosConfig = () => ({
    ...position,
    x: getAbsolutePosition({ total: containerWidth, percentage: position.x }),
    y: getAbsolutePosition({ total: containerHeight, percentage: position.y }),
  })

  const [absolutePosition, setAbsolutePosition] = useState(getPosConfig())

  useEffect(() => {
    if (scrollIntoView !== undefined && scrollIntoView === i) {
      markerChildRef.current?.scrollIntoView({ block: 'center' })
      setScrollIntoView(undefined)
    }
  }, [scrollIntoView, i])

  useEffect(() => {
    setAbsolutePosition(getPosConfig())
  }, [position, containerWidth, containerHeight, placingMode])

  useEffect(() => {
    if (active && currentItemIndex !== i) {
      setCurrentItemIndex(i)
    }
  }, [active, currentItemIndex, i])

  useImperativeHandle(forwardRef, () => markerChildRef.current, [])

  const [draggablePosition, setDraggablePosition] = useState<{ x: number, y: number }>(getPosConfig())

  useEffect(() => {
    setDraggablePosition(getPosConfig())
  }, [pageScaleFactor, containerWidth, containerHeight])

  const handleDrag = useCallback(() => {
    draggedRef.current = true
  }, [])

  const shouldRender = pager?.currentPage === position.page
  if (!shouldRender || signed) return null

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (currentItemIndex !== i) {
      setCurrentItemIndex(i)
    }

    if (draggedRef.current) {
      draggedRef.current = false
      return
    }

    if (onClick && markerChildRef.current?.contains(e.target as Node)) {
      onClick(e)
    }
  }

  const handleDragStart = () => {
    if (currentItemIndex !== i) {
      setCurrentItemIndex(i)
    }
    setIsDragging(true)
    if (onDragStart) {
      onDragStart()
    }
  }

  const handleDragStop = (_e: MouseEvent, data: DraggableData) => {
    setIsDragging(false)
    const xPercent = getRelativePosition({ total: containerWidth, position: data.x })
    const yPercent = getRelativePosition({ total: containerHeight, position: data.y })

    if (onDragStop) {
      onDragStop({ xPercent, yPercent })
    }

    setDraggablePosition({ x: data.x, y: data.y })

    if (activeItem) {
      updateItem({
        ...activeItem,
        position: {
          ...activeItem.position,
          x: xPercent,
          y: yPercent,
          heightPercent: getRelativePosition({ total: pageSize.originalHeight, position: (styleConfig?.heightPx ?? defaultMarkerHeight) }),
          widthPercent: getRelativePosition({ total: pageSize.originalWidth, position: (styleConfig?.heightPx ?? defaultMarkerHeight) * 3 }),
        },
      })
    }
  }

  return (
    <Draggable
      bounds="parent"
      disabled={!placingMode || disabled}
      position={draggablePosition}
      defaultPosition={absolutePosition}
      onDrag={handleDrag}
      onStart={handleDragStart}
      onStop={handleDragStop}
    >
      <div ref={markerChildRef} className={clsx(classes.outer, containerClassName)} onClick={handleClick}>
        <div className={clsx(classes.markerBorder, innerClassName, classes.inner)}>
          {children}
        </div>
      </div>
    </Draggable>
  )
})

export default MarkerBase
